import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from 'app/shared/shared.module';
import { interval } from 'rxjs';
import { AuthFormHeaderComponent } from '../../auth-form-header/auth-form-header.component';
import { MfaChallenge } from '../auth-flow.model';
import { CodeInputComponent } from '../code-input/code-input.component';

const CODE_LENGTH = 6;
const LOW_REMAINING = 2;

@Component({
  selector: 'mh-mfa-step',
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule, AuthFormHeaderComponent, CodeInputComponent],
  templateUrl: './mfa-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw-block' },
})
export class MfaStepComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly challenge = input.required<MfaChallenge>();
  readonly rememberDeviceAllowed = input(false);
  readonly loading = input(false);
  readonly errorKey = input<string | null>(null);

  readonly verified = output<{ code: string; rememberDevice: boolean }>();
  readonly resendRequested = output<void>();
  readonly backRequested = output<void>();

  readonly code = new FormControl('');
  readonly rememberDevice = signal(false);

  readonly expiresIn = signal(0);
  readonly resendIn = signal(0);

  private readonly codeValue = signal('');

  readonly expired = computed(() => this.expiresIn() <= 0);
  readonly countdown = computed(() => {
    const total = Math.max(0, this.expiresIn());
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  });

  readonly outOfResends = computed(() => this.challenge().remainingResends <= 0);
  readonly canResend = computed(() => this.resendIn() <= 0 && !this.outOfResends() && !this.loading());
  readonly canVerify = computed(() => this.codeValue().length === CODE_LENGTH && !this.expired() && !this.loading());

  readonly lowAttempts = computed(() => {
    const remaining = this.challenge().remainingAttempts;
    return isFinite(remaining) && remaining <= LOW_REMAINING ? remaining : null;
  });
  readonly lowResends = computed(() => {
    const remaining = this.challenge().remainingResends;
    return isFinite(remaining) && remaining > 0 && remaining <= LOW_REMAINING ? remaining : null;
  });

  constructor() {
    effect(() => {
      const challenge = this.challenge();
      this.expiresIn.set(challenge.expiresInSeconds);
      this.resendIn.set(challenge.resendAvailableInSeconds);
    });

    this.code.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.codeValue.set(value ?? '');
    });

    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.expiresIn() > 0) this.expiresIn.update((v) => v - 1);
        if (this.resendIn() > 0) this.resendIn.update((v) => v - 1);
      });
  }

  submit(): void {
    if (!this.canVerify()) return;

    this.verified.emit({ code: this.codeValue(), rememberDevice: this.rememberDevice() });
  }

  resend(): void {
    if (!this.canResend()) return;

    this.code.setValue('');
    this.resendRequested.emit();
  }

  toggleRememberDevice(event: Event): void {
    this.rememberDevice.set((event.target as HTMLInputElement).checked);
  }
}
